import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { BigButton } from "../design/BigButton";
import { CheckCircle2, Star } from "lucide-react";

function StarsRow({ n }) {
  return (
    <div className="stars-row" data-testid="stars-row" aria-label={`${n} stars earned`}>
      {[0, 1, 2].map((i) => (
        <Star
          key={i}
          size={18}
          fill={i < n ? "#FFD447" : "transparent"}
          stroke={i < n ? "#FFD447" : "#B7B0AA"}
          aria-hidden
        />
      ))}
    </div>
  );
}

export function LevelSelectScreen() {
  const { profileId, worldSlug } = useParams();
  const navigate = useNavigate();
  const [world, setWorld] = useState(null);
  const [levels, setLevels] = useState([]);
  const [progressByLevel, setProgressByLevel] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get(`/worlds/${worldSlug}/levels`);
      if (cancelled) return;
      setWorld(data.world);
      setLevels(data.items || []);
      // Fetch progress for each playable level in parallel
      const playable = (data.items || []).filter(
        (l) => l.contentStatus === "playable" || l.contentStatus === "polished",
      );
      const results = await Promise.allSettled(
        playable.map((l) =>
          api.get(`/levels/${l.id}/progress`, { params: { childProfileId: profileId } })
        ),
      );
      if (cancelled) return;
      const map = {};
      results.forEach((r, i) => {
        if (r.status === "fulfilled") map[playable[i].id] = r.value.data;
      });
      setProgressByLevel(map);
      setLoading(false);
    })();
    return () => (cancelled = true);
  }, [profileId, worldSlug]);

  return (
    <div className="wmleye-app level-select" data-testid="level-select">
      <header className="level-select__header">
        <BigButton variant="ghost" onClick={() => navigate(`/child/${profileId}/worlds`)} data-testid="level-select-back">
          ← Worlds
        </BigButton>
        <h1>{world?.name || "Levels"}</h1>
      </header>
      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="level-select__grid">
          {levels.map((lv) => {
            const playable = lv.contentStatus !== "draft";
            const prog = progressByLevel[lv.id];
            const stars = prog?.stars || 0;
            const completed = prog?.status === "completed";
            return (
              <button
                key={lv.id}
                className={`level-card${playable ? "" : " level-card--soon"}`}
                onClick={() => playable && navigate(`/child/${profileId}/play/${lv.id}`)}
                disabled={!playable}
                data-testid={`level-card-${lv.slug}`}
                aria-label={`${lv.name}${completed ? ", completed" : ""}${!playable ? ", coming soon" : ""}`}
              >
                <div
                  className="level-card__thumb"
                  style={{ backgroundImage: `url(${lv.sceneImageUrl})` }}
                >
                  {completed && (
                    <div className="level-card__check" aria-hidden data-testid={`level-card-${lv.slug}-check`}>
                      <CheckCircle2 size={28} />
                    </div>
                  )}
                </div>
                <div className="level-card__body">
                  <h3 data-testid={`level-card-${lv.slug}-name`}>{lv.name}</h3>
                  <div className="level-card__diff">{lv.difficulty}</div>
                  {playable ? (
                    <StarsRow n={stars} />
                  ) : (
                    <span className="level-card__badge">Coming soon</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
